// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';

/** A measured series plus its latest value, against the shared `timestamps` axis. */
export interface ChillerPlantSeries {
  current: number | null;
  values: (number | null)[];
}

export interface CoolingLoadChart extends ChillerPlantSeries {
  unit: string;
  currentTons: number | null;
  /**
   * The same fleet sum with no run-status gate. On the GA-10 feed run status is
   * decoupled from power and flow — a chiller reporting "off" still draws
   * ~246 kW — so gating roughly halves the number for undefendable reasons.
   */
  totalAllChillers: (number | null)[];
  currentAllChillers: number | null;
  currentAllChillersTons: number | null;
}

export interface ActiveChillerCountChart extends ChillerPlantSeries {
  /** `twin` when the current count came from the twin snapshot. */
  currentSource: string | null;
  /** `telemetry-cov` — reconstructed from change-of-value transitions, so short
   *  dwells are upstream's reading rather than a rendering artefact. */
  source: string | null;
  totalChillers: number | null;
}

export interface ChilledWaterTempsChart {
  unit: string;
  supplyAvg: ChillerPlantSeries;
  returnAvg: ChillerPlantSeries;
  deltaTAvg: ChillerPlantSeries;
}

export interface ScalarChart extends ChillerPlantSeries {
  unit: string | null;
}

export interface ChillerEntry {
  equipmentId: string;
  label: string | null;
  running: 'on' | 'off' | 'unknown' | null;
  runStatusSource: string | null;
  runStatusJoin: string | null;
  runStatusTimestamp: string | null;
  runStatusQuality: string | null;
  capacityTons: number | null;
  ratedPowerKw: number | null;
  currentLoadKw: number | null;
  currentLoadTons: number | null;
  utilization: number | null;
  excludedReason: string | null;
  flags: Record<string, number> | null;
  /** Only populated with `include=perChiller`. */
  loadKw?: (number | null)[] | null;
  runStatus?: (number | null)[] | null;
}

export interface ChillerPlantCoverage {
  requested: number;
  contributing: number;
  unknown: string[];
  unresolved: string[];
  failed: string[];
  /** Samples above a chiller's nameplate. Counted, not dropped. */
  overNameplateSamples: number;
  fleetCapacityExceeded: boolean;
  reasons: Record<string, unknown>;
}

export interface ChillerPlantData {
  siteId: string;
  window: {
    start: string | null;
    end: string | null;
    resolution: string | null;
    /** Derived from the data, not from upstream's (wrong) `resolution` field. */
    sampleCadenceSeconds: number | null;
  };
  timestamps: string[];
  charts: {
    coolingLoad: CoolingLoadChart;
    activeChillerCount: ActiveChillerCountChart;
    chilledWaterTemps: ChilledWaterTempsChart;
    chilledWaterFlow: ScalarChart;
    electricPower: ScalarChart;
  };
  chillers: ChillerEntry[];
  coverage: ChillerPlantCoverage;
}

export interface ChillerPlantEnvelope {
  data: ChillerPlantData;
}

export interface ChillerPlantQuery {
  /** Window start, ISO-8601 date-time. Defaults server-side to UTC midnight. */
  start?: string;
  /** Window end, ISO-8601 date-time. Defaults server-side to now. */
  end?: string;
  /**
   * Downsample the composed series (ISO-8601 duration, e.g. PT15M), applied
   * after the load is computed so it cannot bias the result.
   */
  resolution?: string;
  /** Per-chiller arrays. Off by default: ~1.2 MB a day against ~180 KB. */
  perChiller?: boolean;
}

/**
 * Every chiller-plant card in one request: the fan-out across nine chillers, the
 * timestamp reconciliation and the cooling-load arithmetic happen in the BFF.
 */
export const getChillerPlant = (
  siteId: string,
  { start, end, resolution, perChiller }: ChillerPlantQuery = {}
): Promise<ChillerPlantEnvelope> => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (resolution) params.set('resolution', resolution);
  if (perChiller) params.set('include', 'perChiller');

  const base = ENDPOINTS.LIVE_CHILLER_PLANT(siteId);
  const query = params.toString();
  return get<ChillerPlantEnvelope>(query ? `${base}?${query}` : base);
};
